import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform, AnimatePresence } from "framer-motion";
import { Phone, MessageCircle, ArrowRight, Award, ShieldCheck, Clock, BadgeCheck } from "lucide-react";
import { MaskedLine, Reveal } from "../components/Reveal";
import { Marquee } from "../components/Marquee";
import { PhotoSlot } from "../components/PhotoSlot";
import { PHONE_TEL, PHONE_DISPLAY, waLink } from "../lib/contact";
import { SERVICES, PROCESS_STEPS, WHY_US, HERO_IMAGE } from "../lib/content";

const INTRO_DURATION = 1500;
const HERO_DELAY = 1.35;

const whyIcons = { award: Award, shield: ShieldCheck, clock: Clock, badge: BadgeCheck };

const IntroOverlay = () => {
  const [done, setDone] = useState(false);
  useEffect(() => {
    const t = setTimeout(() => setDone(true), INTRO_DURATION);
    return () => clearTimeout(t);
  }, []);
  return (
    <AnimatePresence>
      {!done && (
        <motion.div
          data-testid="intro-overlay"
          className="fixed inset-0 z-[100] grid place-items-center bg-anthracite"
          exit={{ y: "-100%" }}
          transition={{ duration: 0.85, ease: [0.76, 0, 0.24, 1] }}
        >
          <div className="text-center">
            <div className="overflow-hidden px-6">
              <motion.img
                src="/assets/logo-light.png"
                alt="LOGITEH PROD · Mobilă din viitor"
                initial={{ y: "115%" }}
                animate={{ y: 0 }}
                transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
                className="mx-auto w-64 sm:w-80"
              />
            </div>
            <motion.div
              initial={{ scaleX: 0 }}
              animate={{ scaleX: 1 }}
              transition={{ duration: 0.9, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
              className="mx-auto mt-4 h-px w-40 origin-left bg-oak"
            />
            <motion.p
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.55 }}
              className="mt-3 font-mono text-[10px] uppercase tracking-[0.45em] text-cream/50"
            >
              Tâmplărie & mobilier la comandă
            </motion.p>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const imgY = useTransform(scrollYProgress, [0, 1], [0, 130]);
  const textY = useTransform(scrollYProgress, [0, 1], [0, 60]);

  return (
    <section
      ref={ref}
      data-testid="hero-section"
      className="grain-overlay wood-ring relative overflow-hidden bg-anthracite text-cream"
    >
      <div className="ruler-ticks pointer-events-none absolute bottom-0 left-4 top-0 hidden w-px lg:block" />
      <div className="pointer-events-none absolute -right-24 top-24 select-none font-serif text-[22rem] font-black leading-none text-cream/[0.03]">
        LP
      </div>

      <div className="relative z-[2] mx-auto grid max-w-7xl gap-14 px-5 pb-24 pt-36 sm:px-8 lg:grid-cols-12 lg:items-center lg:pb-32 lg:pt-44">
        <motion.div style={{ y: textY }} className="lg:col-span-7">
          <MaskedLine delay={HERO_DELAY}>
            <span
              data-testid="hero-badge"
              className="mb-6 inline-flex items-center gap-3 border border-oak/40 px-4 py-2 font-mono text-[10px] uppercase tracking-[0.35em] text-oak"
            >
              <span className="h-1.5 w-1.5 rotate-45 bg-oak" />
              Atelier de tâmplărie & mobilier personalizat
            </span>
          </MaskedLine>

          <h1 data-testid="hero-title" className="font-serif text-4xl font-black leading-[1.06] tracking-tight sm:text-5xl lg:text-6xl">
            <MaskedLine delay={HERO_DELAY + 0.12}>Mobilier la comandă</MaskedLine>
            <MaskedLine delay={HERO_DELAY + 0.24}>
              și tâmplărie <em className="font-bold italic text-oak">de calitate</em>
            </MaskedLine>
            <MaskedLine delay={HERO_DELAY + 0.36}>
              <span className="text-cream/90">| LOGITEH PROD</span>
            </MaskedLine>
          </h1>

          <motion.p
            data-testid="hero-subtitle"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: HERO_DELAY + 0.55, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-7 max-w-xl text-base leading-relaxed text-cream/70 sm:text-lg"
          >
            De la idee la montaj, executăm mobilier personalizat exact pe dimensiunile
            spațiului tău, din lemn masiv, MDF și PAL.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: HERO_DELAY + 0.7, duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 flex flex-col gap-4 sm:flex-row"
          >
            <a
              href={PHONE_TEL}
              data-testid="hero-call-now-button"
              className="group flex h-14 items-center justify-center gap-3 rounded-full bg-oak px-8 text-sm font-bold uppercase tracking-[0.12em] text-anthracite transition-all duration-300 hover:-translate-y-1 hover:shadow-[0_18px_40px_-10px_rgba(200,138,75,0.5)]"
            >
              <Phone size={17} className="transition-transform duration-300 group-hover:-rotate-12" />
              Sună acum: {PHONE_DISPLAY}
            </a>
            <a
              href={waLink("Bună ziua! Vă trimit schița pentru o ofertă de mobilier la comandă.")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-button"
              className="group flex h-14 items-center justify-center gap-3 rounded-full border border-cream/25 px-8 text-sm font-bold uppercase tracking-[0.12em] text-cream transition-all duration-300 hover:-translate-y-1 hover:border-[#25D366] hover:bg-[#25D366] hover:text-anthracite"
            >
              <MessageCircle size={17} />
              Trimite schița pe WhatsApp
            </a>
          </motion.div>

          <motion.ul
            data-testid="hero-trust-pills"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: HERO_DELAY + 0.9, duration: 0.8 }}
            className="mt-12 flex flex-wrap gap-x-7 gap-y-3"
          >
            {["Proiecte 100% personalizate", "Atelier propriu de execuție", "Transport & montaj", "Garanție pentru fiecare lucrare"].map(
              (t) => (
                <li key={t} className="flex items-center gap-2 font-mono text-[11px] uppercase tracking-[0.18em] text-cream/50">
                  <span className="h-1 w-1 rotate-45 bg-oak" /> {t}
                </li>
              )
            )}
          </motion.ul>
        </motion.div>

        <div className="relative lg:col-span-5">
          <motion.div
            initial={{ opacity: 0, y: 60 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: HERO_DELAY + 0.5, duration: 1, ease: [0.16, 1, 0.3, 1] }}
          >
            <motion.figure style={{ y: imgY }} className="relative">
              <span className="absolute -left-4 -top-4 h-full w-full border border-oak/50" aria-hidden="true" />
              <div className="relative aspect-[4/5] overflow-hidden">
                <img
                  src={HERO_IMAGE}
                  alt="Recepție comercială cu LED și riflaj din lemn · proiect real LOGITEH PROD"
                  data-testid="hero-image"
                  className="h-full w-full object-cover"
                />
                <div className="absolute inset-0 bg-[radial-gradient(ellipse_at_50%_30%,transparent_40%,rgba(24,21,18,0.55)_100%)]" />
              </div>
              <figcaption className="absolute bottom-4 left-4 right-4 flex items-center justify-between border-t border-cream/20 pt-3 font-mono text-[10px] uppercase tracking-[0.3em] text-cream/80">
                <span>Recepție comercială · LED & riflaj</span>
                <span className="text-oak">Proiect real</span>
              </figcaption>
            </motion.figure>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const ServicesPreview = () => (
  <section data-testid="services-preview" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
    <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-walnut">Servicii & ce realizăm</p>
        <h2 className="mt-4 max-w-2xl font-serif text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">
          Meșteșug care se vede <em className="italic text-walnut">în fiecare îmbinare</em>
        </h2>
      </Reveal>
      <Reveal delay={0.15}>
        <Link
          to="/servicii"
          data-testid="services-preview-all-link"
          className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-walnut transition-colors hover:text-oak"
        >
          Toate serviciile
          <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
        </Link>
      </Reveal>
    </div>

    <div className="mt-14 grid gap-px overflow-hidden rounded-sm border border-line bg-line sm:grid-cols-2 lg:grid-cols-3">
      {SERVICES.map((s, i) => (
        <Reveal key={s.id} delay={i * 0.08} className="h-full">
          <Link
            to="/servicii"
            data-testid={`service-card-${s.id}`}
            className="group flex h-full flex-col justify-between gap-10 bg-white p-8 transition-colors duration-500 hover:bg-anthracite"
          >
            <div>
              <span className="font-serif text-4xl font-black text-oak/40 transition-colors duration-500 group-hover:text-oak">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="mt-5 font-serif text-xl font-bold text-ink transition-colors duration-500 group-hover:text-cream sm:text-2xl">
                {s.title}
              </h3>
              <p className="mt-3 text-sm leading-relaxed text-clay transition-colors duration-500 group-hover:text-cream/60">
                {s.description}
              </p>
            </div>
            <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-walnut transition-colors duration-500 group-hover:text-oak">
              Detalii <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1.5" />
            </span>
          </Link>
        </Reveal>
      ))}
      <Reveal delay={0.4} className="h-full">
        <Link
          to="/contact"
          data-testid="service-card-cta"
          className="group flex h-full flex-col justify-between gap-10 bg-oak p-8 transition-colors duration-500 hover:bg-walnut"
        >
          <h3 className="font-serif text-2xl font-bold leading-snug text-anthracite transition-colors duration-500 group-hover:text-cream">
            Ai un proiect în minte? Hai să-l discutăm.
          </h3>
          <span className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.25em] text-anthracite transition-colors duration-500 group-hover:text-cream">
            Cere ofertă <ArrowRight size={13} className="transition-transform duration-300 group-hover:translate-x-1.5" />
          </span>
        </Link>
      </Reveal>
    </div>
  </section>
);

const Process = () => (
  <section data-testid="process-section" className="grain-overlay wood-ring bg-anthracite py-24 text-cream lg:py-32">
    <div className="relative z-[2] mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-oak">Cum lucrăm</p>
        <h2 className="mt-4 max-w-2xl font-serif text-3xl font-bold tracking-tight sm:text-4xl lg:text-5xl">
          Patru pași simpli, <em className="italic text-oak">un rezultat impecabil</em>
        </h2>
      </Reveal>

      <div className="mt-16">
        {PROCESS_STEPS.map((p, i) => (
          <Reveal key={p.step} delay={i * 0.06}>
            <div
              data-testid={`process-step-${p.step}`}
              className="group grid items-baseline gap-4 border-t border-cream/10 py-10 transition-colors duration-500 last:border-b hover:border-oak/40 sm:grid-cols-12 sm:gap-8"
            >
              <span className="font-serif text-6xl font-black leading-none text-cream/10 transition-colors duration-500 group-hover:text-oak/60 sm:col-span-2 lg:text-7xl">
                {p.step}
              </span>
              <h3 className="font-serif text-2xl font-bold sm:col-span-4 lg:text-3xl">{p.title}</h3>
              <p className="text-sm leading-relaxed text-cream/60 sm:col-span-6 sm:text-base">{p.description}</p>
            </div>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const WhyUs = () => (
  <section data-testid="why-us-section" className="bg-sand py-24 lg:py-32">
    <div className="mx-auto max-w-7xl px-5 sm:px-8">
      <Reveal className="text-center">
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-walnut">De ce LOGITEH PROD</p>
        <h2 className="mx-auto mt-4 max-w-2xl font-serif text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">
          Încredere construită <em className="italic text-walnut">lucrare cu lucrare</em>
        </h2>
      </Reveal>

      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
        {WHY_US.map((f, i) => {
          const Icon = whyIcons[f.icon];
          return (
            <Reveal key={f.title} delay={i * 0.1} className="h-full">
              <div
                data-testid={`why-us-card-${f.icon}`}
                className="group h-full border border-line bg-white p-7 transition-all duration-500 hover:-translate-y-1.5 hover:border-oak/60 hover:shadow-[0_18px_40px_-14px_rgba(110,71,43,0.25)]"
              >
                <span className="grid h-12 w-12 place-items-center rounded-full bg-sand text-walnut transition-colors duration-500 group-hover:bg-oak group-hover:text-anthracite">
                  <Icon size={20} strokeWidth={1.75} />
                </span>
                <h3 className="mt-6 font-serif text-lg font-bold text-ink">{f.title}</h3>
                <p className="mt-3 text-sm leading-relaxed text-clay">{f.desc}</p>
              </div>
            </Reveal>
          );
        })}
      </div>
    </div>
  </section>
);

const PortfolioTeaser = () => (
  <section data-testid="portfolio-teaser" className="mx-auto max-w-7xl px-5 py-24 sm:px-8 lg:py-32">
    <div className="flex flex-col justify-between gap-6 lg:flex-row lg:items-end">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-walnut">Galerie foto / portofoliu</p>
        <h2 className="mt-4 max-w-2xl font-serif text-3xl font-bold tracking-tight text-ink sm:text-4xl lg:text-5xl">
          Lucrările noastre, <em className="italic text-walnut">direct din atelier</em>
        </h2>
      </Reveal>
      <Reveal delay={0.15}>
        <Link
          to="/portofoliu"
          data-testid="portfolio-teaser-link"
          className="group inline-flex items-center gap-2 font-mono text-xs uppercase tracking-[0.25em] text-walnut transition-colors hover:text-oak"
        >
          Vezi portofoliul
          <ArrowRight size={15} className="transition-transform duration-300 group-hover:translate-x-1.5" />
        </Link>
      </Reveal>
    </div>
    <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-4">
      {[
        { category: "Bucătării", label: "Bucătărie cu LED", src: "/assets/real-bucatarie-completa.jpg" },
        { category: "Dressinguri", label: "Noptieră dormitor", src: "/assets/real-noptiera.jpg" },
        { category: "Lemn Masiv", label: "Balustradă din stejar", src: "/assets/real-balustrada.jpg" },
        { category: "Altele", label: "Recepție comercială", src: "/assets/real-receptie.jpg" },
      ].map((s, i) => (
        <Reveal key={s.label} delay={i * 0.08}>
          <PhotoSlot index={i + 1} category={s.category} label={s.label} src={s.src} />
        </Reveal>
      ))}
    </div>
  </section>
);

const CtaBand = () => (
  <section data-testid="cta-band" className="grain-overlay wood-ring bg-anthracite py-24 text-center text-cream lg:py-28">
    <div className="relative z-[2] mx-auto max-w-3xl px-5">
      <Reveal>
        <p className="font-mono text-xs uppercase tracking-[0.35em] text-oak">Hai să vorbim despre proiectul tău</p>
        <a
          href={PHONE_TEL}
          data-testid="cta-band-phone"
          className="mt-6 block font-serif text-5xl font-black tracking-tight transition-colors duration-300 hover:text-oak sm:text-6xl lg:text-7xl"
        >
          {PHONE_DISPLAY}
        </a>
        <p className="mx-auto mt-6 max-w-lg text-sm leading-relaxed text-cream/60 sm:text-base">
          Trimite-ne dimensiunile aproximative sau o poză pe WhatsApp pentru o cotație de
          preț rapidă. Răspundem prompt.
        </p>
        <div className="mt-10 flex flex-col justify-center gap-4 sm:flex-row">
          <a
            href={PHONE_TEL}
            data-testid="cta-band-call-button"
            className="flex h-14 items-center justify-center gap-3 rounded-full bg-oak px-8 text-sm font-bold uppercase tracking-[0.12em] text-anthracite transition-all duration-300 hover:-translate-y-1"
          >
            <Phone size={17} /> Sună acum
          </a>
          <Link
            to="/contact"
            data-testid="cta-band-quote-button"
            className="flex h-14 items-center justify-center gap-3 rounded-full border border-cream/25 px-8 text-sm font-bold uppercase tracking-[0.12em] transition-all duration-300 hover:-translate-y-1 hover:border-oak hover:text-oak"
          >
            Cere ofertă online
          </Link>
        </div>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <main data-testid="home-page">
      <IntroOverlay />
      <Hero />
      <Marquee />
      <ServicesPreview />
      <Process />
      <WhyUs />
      <PortfolioTeaser />
      <CtaBand />
    </main>
  );
}
